import { VIEW_FOLLOW_MIN_JUMP_BARS, VIEW_SCROLL_ANIMATION_MS } from "../constants";
import { easeOutProgress, isRunning, interpolate } from "./transition";

/**
 * View between two views of a transition. The zoom changes evenly on a log
 * scale, the empty space right of the last bar evenly in pixels, so the
 * view never swings past either end (with the same zoom it is a plain
 * slide of the right offset).
 *
 * @param {{ barSpacing: number, rightOffset: number }} from
 * @param {{ barSpacing: number, rightOffset: number }} to
 * @param {number} progress - 0..1
 * @returns {{ barSpacing: number, rightOffset: number }}
 */
const viewBetween = (from, to, progress) => {
  const barSpacing = Math.exp(
    interpolate(Math.log(from.barSpacing), Math.log(to.barSpacing), progress),
  );
  const rightOffsetPx = interpolate(
    from.rightOffset * from.barSpacing,
    to.rightOffset * to.barSpacing,
    progress,
  );

  return { barSpacing, rightOffset: rightOffsetPx / barSpacing };
};

/**
 * Smooth movement of the view: the time scale's zoom and right offset go to
 * a target over VIEW_SCROLL_ANIMATION_MS with ease-out. Used when the view
 * follows a new bar (the whole bar, or what time scrolling missed), scrolls
 * to the latest bars and shows a visible span.
 *
 * @param {object} timeScale
 */
export const createScrollMotion = (timeScale) => {
  // { from, to, startTime, duration } of the view { barSpacing, rightOffset }
  let transition = null;

  const startFrom = (from, to, now) => {
    timeScale.setView(from);
    transition = { from, to, startTime: now, duration: VIEW_SCROLL_ANIMATION_MS };
  };

  const finish = () => {
    if (transition) timeScale.setView(transition.to);

    transition = null;
  };

  return {
    isActive: (now) => isRunning(transition, now),

    /**
     * @param {{ barSpacing: number, rightOffset: number }} view - px, bars between
     *   the last bar and the price axis
     * @param {{ isAnimated: boolean, now: number }} params
     */
    moveTo: (view, { isAnimated, now }) => {
      if (isAnimated) {
        const { barSpacing, rightOffset } = timeScale;

        startFrom({ barSpacing, rightOffset }, view, now);
        return;
      }

      transition = null;
      timeScale.setView(view);
    },

    /**
     * Adds a bar on the right (TimeScale.appendBar). When the view follows
     * it, the bars first stay in place and then slide left by `jump` bars:
     * one bar when the view stood still, only the miss of the time phase
     * when it scrolled with time (viewFollow.appendBar).
     *
     * @param {{ isAnimated: boolean, now: number, isFollowing: boolean, jump: number }} params
     */
    appendBar: ({ isAnimated, now, isFollowing, jump }) => {
      finish();

      const { barSpacing, rightOffset } = timeScale;

      timeScale.appendBar(isFollowing);

      if (isAnimated && isFollowing && Math.abs(jump) >= VIEW_FOLLOW_MIN_JUMP_BARS) {
        startFrom({ barSpacing, rightOffset: rightOffset - jump }, { barSpacing, rightOffset }, now);
      }
    },

    /** @param {number} now - ms */
    step: (now) => {
      if (!transition) return;

      // The end is set exactly, without the rounding of the log scale
      if (!isRunning(transition, now)) {
        finish();
        return;
      }

      timeScale.setView(
        viewBetween(transition.from, transition.to, easeOutProgress(transition, now)),
      );
    },

    /** Jumps to the target. */
    finish,

    /** Stops where the view is now: a gesture took over. */
    cancel: () => {
      transition = null;
    },
  };
};
